import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';

export interface ManagedUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  phone_number: string | null;
  show_landing_page: string;
  client: number | null;
  team: number | null;
  client_name: string | null;
  team_name: string | null;
  hubspot_id: string;
  groups: number[];
  groups_detail: {id: number; name: string}[];
}

export interface ActivityActor {
  id: number;
  display_name: string | null;
  email: string;
}

export type ActivityRow =
  | {
      kind: 'auth';
      id: number;
      timestamp: string;
      event_type: string;
      actor: ActivityActor | null;
      detail: Record<string, unknown>;
    }
  | {
      kind: 'change';
      id: number;
      timestamp: string;
      action: number;
      object: {type: string; pk: string; repr: string} | null;
      changes_display_dict: Record<string, [string, string]>;
      actor: ActivityActor | null;
      metadata: {celery_task_class?: string} | null;
    };

export interface ActivityParams {
  id: number;
  cursor?: string;
  limit?: number;
  actor_only?: boolean;
  event_type?: string;
  action?: number;
  timestamp__gte?: string;
  timestamp__lte?: string;
  search?: string;
}

export interface ActivityPage {
  results: ActivityRow[];
  next_cursor: string | null;
}

export interface ListUsersParams {
  search?: string;
  is_active?: boolean;
  // Repeated as `?client=1&client=2`; the API ORs them together.
  client?: number[];
  offset?: number;
  limit?: number;
}

interface IState {
  users: ManagedUser[];
  count: number;
  loadingList: boolean;
  listError: string | null;
  selectedUser: ManagedUser | null;
  loadingDetail: boolean;
  detailError: string | null;
  // Id of the most recent getUser request, so a late response for a user the admin has already
  // navigated away from cannot overwrite the current one.
  requestedUserId: number | null;
}

const toQueryString = (params: Record<string, any>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    // An array becomes a repeated param, which is how django-filter reads multi-value filters.
    if (Array.isArray(value)) {
      value.forEach(entry => query.append(key, String(entry)));
      return;
    }
    query.append(key, String(value));
  });
  return query.toString();
};

export const userManagementEndpoints = {
  listUsers: createAsyncThunk('userManagement/listUsers', async (params: ListUsersParams, {rejectWithValue}) => {
    try {
      const response = await ApiService.get(`/user_management/?${toQueryString(params)}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }),
  getUserActivity: createAsyncThunk(
    'userManagement/getUserActivity',
    async ({id, ...params}: ActivityParams, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/user_management/${id}/activity/?${toQueryString(params)}`);
        return response.data as ActivityPage;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  getUser: createAsyncThunk('userManagement/getUser', async (id: number, {rejectWithValue}) => {
    try {
      const response = await ApiService.get(`/user_management/${id}/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }),
  updateUser: createAsyncThunk(
    'userManagement/updateUser',
    async ({id, data}: {id: number; data: Partial<ManagedUser>}, {rejectWithValue}) => {
      try {
        const response = await ApiService.patch(`/user_management/${id}/`, data);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  inviteUser: createAsyncThunk(
    'userManagement/inviteUser',
    async (payload: {email: string; client?: number; groups?: number[]}, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/user_management/invite/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  createUserWithTempPassword: createAsyncThunk(
    'userManagement/createUserWithTempPassword',
    async (
      payload: {
        email: string;
        first_name: string;
        last_name: string;
        client: number;
        team?: number | null;
        groups?: number[];
      },
      {rejectWithValue},
    ) => {
      try {
        const response = await ApiService.post('/user_management/create_with_temp_password/', payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data);
      }
    },
  ),
  resetPassword: createAsyncThunk('userManagement/resetPassword', async (id: number, {rejectWithValue}) => {
    try {
      const response = await ApiService.post(`/user_management/${id}/reset_password/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }),
};

export const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState: {
    users: [],
    count: 0,
    loadingList: false,
    listError: null,
    selectedUser: null,
    loadingDetail: false,
    detailError: null,
    requestedUserId: null,
  } as IState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(userManagementEndpoints.listUsers.pending, state => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(userManagementEndpoints.listUsers.fulfilled, (state, {payload}) => {
        state.loadingList = false;
        state.users = payload.results ?? payload;
        state.count = payload.count ?? state.users.length;
      })
      .addCase(userManagementEndpoints.listUsers.rejected, state => {
        state.loadingList = false;
        // Without this an error renders as "No users found." -- indistinguishable from empty.
        state.listError = 'Could not load users.';
      })
      .addCase(userManagementEndpoints.getUser.pending, (state, {meta}) => {
        state.loadingDetail = true;
        state.detailError = null;
        state.requestedUserId = meta.arg;
      })
      .addCase(userManagementEndpoints.getUser.fulfilled, (state, {payload}) => {
        if (payload.id !== state.requestedUserId) return;
        state.loadingDetail = false;
        state.selectedUser = payload;
      })
      .addCase(userManagementEndpoints.getUser.rejected, (state, {meta}) => {
        if (meta.arg !== state.requestedUserId) return;
        state.loadingDetail = false;
        state.detailError = 'Could not load this user.';
      })
      .addCase(userManagementEndpoints.updateUser.fulfilled, (state, {payload}) => {
        // A save that lands after the admin selected someone else must not replace the
        // currently-displayed user, or the pane sticks on "Loading..." forever.
        if (payload.id === state.requestedUserId) state.selectedUser = payload;
        const index = state.users.findIndex(user => user.id === payload.id);
        if (index !== -1) state.users[index] = payload;
      });
  },
});

export type UserManagementSlice = {
  [userManagementSlice.name]: ReturnType<(typeof userManagementSlice)['reducer']>;
};
